import { Option } from '@sapphire/result';
import { SurveyMetadataFormState } from '@ps/survey-utils';

interface SelectedLocale {
  countries: string[];
  languages: string[];
}

/**
 * Checks whether the selected countries or languages have actually changed.
 *
 * Either side of a comparison being empty is that in-flight recompute,
 * never a real edit — countries are required in edit mode, so an empty list can't be a
 * deliberate choice either.
 */
export function hasLocaleChanged(
  previous: SurveyMetadataFormState,
  next: SurveyMetadataFormState,
): boolean {
  return selectedLocaleOf(previous)
    .zip(selectedLocaleOf(next))
    .mapOr(
      false,
      ([previousLocale, nextLocale]) =>
        !sameLocaleCodes(previousLocale.countries, nextLocale.countries) ||
        !sameLocaleCodes(previousLocale.languages, nextLocale.languages),
    );
}

function selectedLocaleOf(
  metadata: SurveyMetadataFormState,
): Option<SelectedLocale> {
  return Option.from(metadata.value).andThen(
    ({ selectedCountries, selectedLanguages }) =>
      selectedCountries.length && selectedLanguages.length
        ? Option.some({
            countries: selectedCountries,
            languages: selectedLanguages,
          })
        : Option.none,
  );
}

function sameLocaleCodes(previous: string[], next: string[]): boolean {
  return (
    previous.length === next.length &&
    sortLocaleCodes([...previous]).join(',') ===
      sortLocaleCodes([...next]).join(',')
  );
}

function sortLocaleCodes(codes: string[]): string[] {
  return [...codes].sort((a, b) => a.localeCompare(b));
}
